import './Login.css';
import React from 'react';
import { Button } from '../../components/Button/Button';
import { DBInspector } from '../../components/DBInspector/DBInspector';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { FormTextField } from '../../components/Form/TextField/FormTextField';
import { FormCheckbox } from '../../components/Form/Checkbox/FormCheckbox';
import { handleError } from '../../model/notification';
import { t } from '../../locales/translate';
import { Stack } from '../../components/Stack/Stack';
import { useMeta } from '../../model/meta';
import { NavLink, useHistory } from 'react-router-dom';
import { useAsyncValue } from '../../model/data';
import { acceptSignupInvite, getInvite } from '../../model/invite';
import { useRouterParam } from '../../model/history';
import { refreshSession } from '../../model/session';
import { companyPath, invitesPath } from '../../routes';
import { Gatekeeper } from '../Gatekeeper/Gatekeeper';
import { selectMembership } from '../../model/membership';

export interface Signup {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  password?: string | null;
  confirmPassword?: string | null;
  tos?: boolean;
}

export function Signup(): JSX.Element {
  const { token } = useRouterParam<{ token: string }>();
  const [data] = useAsyncValue(() => getInvite(token), [token]);
  const history = useHistory();
  const { model, fields, onSubmit, valid, submissionStatus } = useForm<Signup>({
    model: {},
    onSubmit: async () => {
      const { email, firstName, lastName, password } = model;

      if (token && email && password && firstName && lastName) {
        const resp = await acceptSignupInvite({
          inviteToken: token,
          email,
          firstName,
          lastName,
          password,
        });

        if (resp?.id && resp?.companyId) {
          selectMembership(resp.id, false);
          await refreshSession();
          history.push(companyPath(resp.companyId));
        }
      }
    },
    onSubmitError: (error) => {
      handleError(error);
    },
    validations: {
      email: ['required', 'email'],
      password: 'required',
      confirmPassword: (form) => {
        if (!form.model.confirmPassword) return ['required-field'];
        return form.model.password !== form.model.confirmPassword ? ['password-missmatch'] : [];
      },
      tos: 'required',
      firstName: 'required',
      lastName: 'required',
    },
  });
  useMeta({ title: t.page.login.loginFormTitle, themeColor: '#ededed' });

  return (
    <Gatekeeper>
      <form autoComplete="on">
        <div className="login-form__content">
          <Stack size="sm">
            <Stack size="xxs">
              <h1>{t.page.signup.signupFormTitle}</h1>
              {data && (
                <small
                  dangerouslySetInnerHTML={{
                    __html: [t.page.signup.userHasInvitedYou(data), t.page.signup.pleaseSignUp].join(' '),
                  }}
                ></small>
              )}
            </Stack>
            <FormTextField
              label={t.model.user.email}
              type="email"
              icon="at-sign--sm"
              autoFocus={true}
              inputId="email"
              {...fields.email}
            />
            <FormTextField label={t.model.user.firstName} {...fields.firstName} />
            <FormTextField label={t.model.user.lastName} {...fields.lastName} />
            <Stack size="xxs">
              <FormTextField
                inputId="password"
                name="password"
                label={t.page.signup.password}
                icon="lock--sm"
                type="password"
                {...fields.password}
              />
              <FormTextField
                label={t.page.signup.confirmPassword}
                icon="lock--sm"
                type="password"
                {...fields.confirmPassword}
                required
              />
            </Stack>
            <FormCheckbox verticalAlign="top" label={`${t.page.signup.tos}`} {...fields.tos} />
            <div />
            <Stack size="xs">
              <Button
                type="primary"
                isSubmit
                onClick={onSubmit}
                block
                title={t.page.signup.title}
                loading={submissionStatus === 'submitting'}
                disabled={!valid}
              />
            </Stack>
            <p className="text-center">
              {t.page.signup.alreadyHaveAccount}{' '}
              <NavLink className="link link--strong" to={invitesPath(token)}>
                {t.page.login.title}
              </NavLink>
            </p>
          </Stack>
        </div>
      </form>
      <DBInspector />
    </Gatekeeper>
  );
}
